import { ApolloError } from '@apollo/client';

import {
  MarketingAttributionModel,
  MarketingChannel,
  MarketingDimension,
  MarketingFilter,
  MarketingGranularity,
} from 'core/graphql';

import {
  IMarketingFilter,
  MarketingDimensionCode,
  MarketingGranularityCode,
} from './types';

/**
 * ТЗ 0088 — стык экранов раздела «Маркетинг» с кодогенерацией.
 *
 * Ответы запросов типы экранов (`types.ts`) принимают присваиванием — enum
 * кодогенерации (`MarketingChannel.Meta = 'META'`) совместим со строковым
 * союзом по значению, и `tsc` сверяет каждое поле. Обратно — в переменные
 * запроса — строковый союз в enum сам не превращается: это делают помощники
 * ниже, в одном месте.
 */

/** Фильтр экрана → `MarketingFilter` запроса. */
export const marketingGqlFilter = (
  filter: IMarketingFilter,
): MarketingFilter => ({
  dateFrom: filter.dateFrom,
  dateTo: filter.dateTo,
  channels: filter.channels?.length
    ? (filter.channels as MarketingChannel[])
    : null,
  familySlugs: filter.familySlugs?.length ? filter.familySlugs : null,
  offers: filter.offers?.length ? filter.offers : null,
  campaignIds: filter.campaignIds?.length ? filter.campaignIds : null,
  adsetIds: filter.adsetIds?.length ? filter.adsetIds : null,
  legalEntityId: filter.legalEntityId || null,
  model: (filter.model ?? null) as MarketingAttributionModel | null,
});

export const marketingGqlDimension = (
  dimension: MarketingDimensionCode,
): MarketingDimension => dimension as MarketingDimension;

export const marketingGqlGranularity = (
  granularity: MarketingGranularityCode,
): MarketingGranularity => granularity as MarketingGranularity;

/**
 * Код отказа запроса из `extensions.code` (`window_too_long`, `bad_order`…).
 * `null` — ошибки нет или она без кода (сеть, права): экран покажет общий
 * текст.
 */
export const marketingQueryErrorCode = (
  error: ApolloError | null | undefined,
): string | null => {
  if (!error) return null;
  const coded = (error.graphQLErrors ?? []).find(
    (item) => typeof item?.extensions?.code === 'string',
  );
  return coded ? String(coded.extensions?.code) : null;
};

/** Ошибка поля из `customErrors` мутации. */
export interface IMarketingFieldError {
  field: string;
  code: string;
  message: string;
}

type RawErrors =
  | ReadonlyArray<{
      field?: string | null;
      code?: string | null;
      message?: string | null;
    } | null>
  | null
  | undefined;

export const toMarketingFieldErrors = (
  raw: RawErrors,
): IMarketingFieldError[] =>
  (raw ?? [])
    .filter((error): error is NonNullable<typeof error> => Boolean(error))
    .map((error) => ({
      field: error.field ?? '',
      code: error.code ?? '',
      message: error.message ?? '',
    }));
